import React, { useCallback, useEffect, useMemo, useState } from "react";
import { listOperationalCustomerNotes } from "../../../api/operationsCustomersApi";
import { clean, shortDate } from "../customerFormat";
import { frIdOf, TabState } from "./tabShared";

const toRows = (v) => (Array.isArray(v) ? v : Array.isArray(v?.results) ? v.results : []);
const PAGE = 50;

// FieldRoutes notes (read-only here — they're written in FieldRoutes or by the tech),
// then the Abon notes on the customer record (saved with the footer's Save).
function FieldRoutesNotes({ frId, held }) {
  const [rows, setRows] = useState([]);
  const [next, setNext] = useState(null);
  const [state, setState] = useState("loading");
  const [category, setCategory] = useState("all");

  const load = useCallback(
    async (page) => {
      const { data } = await listOperationalCustomerNotes(frId, { page, page_size: PAGE });
      return { rows: toRows(data), hasNext: Boolean(data?.next) };
    },
    [frId]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setState("loading");
      try {
        const first = await load(1);
        if (!alive) return;
        setRows(first.rows);
        setNext(first.hasNext ? 2 : null);
        setState("ok");
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [load]);

  const more = async () => {
    const page = await load(next);
    setRows((r) => [...r, ...page.rows]);
    setNext(page.hasNext ? next + 1 : null);
  };

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => clean(r.note_category)).filter(Boolean))),
    [rows]
  );
  const shown = category === "all" ? rows : rows.filter((r) => clean(r.note_category) === category);

  return (
    <section className="cov-card">
      <div className="cov-card__head">
        <span>FieldRoutes notes</span>
        <span className="cov-card__count">{held ?? rows.length}</span>
      </div>
      <div className="cov-card__body">
        {state === "loading" ? <div className="cov-empty">Loading notes…</div> : null}
        {state === "error" ? <div className="cov-empty">Couldn't load FieldRoutes notes.</div> : null}
        {state === "ok" && rows.length === 0 ? <div className="cov-empty">No FieldRoutes notes for this customer.</div> : null}
        {categories.length > 1 ? (
          <div className="cov-facts">
            {["all", ...categories].map((c) => (
              <button
                key={c}
                type="button"
                className={`ct-filter ${category === c ? "is-active" : ""}`}
                onClick={() => setCategory(c)}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
        ) : null}
        {shown.map((n) => (
          <div key={n.external_id} className="cov-row">
            <div className="cov-row__main ct-row-main-wide">
              <span className="cov-row__meta">
                {[clean(n.note_category), shortDate(n.note_date)].filter(Boolean).join(" · ")}
              </span>
              <span className="ct-note ct-note--full">{clean(n.body)}</span>
            </div>
          </div>
        ))}
        {next ? (
          <button type="button" className="ct-more" onClick={more}>
            Show older notes
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default function NotesTab({ record, formData = {}, onFieldChange }) {
  const isLead = record?.object === "lead";
  const frId = isLead ? null : frIdOf(record);

  // Abon notes live on the customer's own `notes` field — NOT access_notes.
  // Access & Safety owns access_notes; the two must not share a box. (Legacy
  // notes that landed in extended_fields.notes are still shown as a fallback so
  // nothing already written disappears.)
  const notesValue = isLead
    ? formData.notes ?? formData.message ?? ""
    : formData.notes ??
      record?.raw?.notes ??
      formData.extended_fields?.notes ??
      record?.raw?.extended_fields?.notes ??
      "";

  const followupValue = formData.extended_fields?.follow_up ?? record?.raw?.extended_fields?.follow_up ?? "";

  const update = (key, value) => {
    if (typeof onFieldChange !== "function") return;
    onFieldChange(key, value);
  };

  if (!record) return <TabState>Loading…</TabState>;

  return (
    <div className="ct-stack">
      {frId ? <FieldRoutesNotes frId={frId} held={record?.raw?.fieldroutes?.notes?.held} /> : null}

      <section className="cov-card">
        <div className="cov-card__head">
          <span>{isLead ? "Lead notes" : "Abon notes"}</span>
        </div>
        <div className="cov-card__body">
          <textarea
            className="ct-textarea"
            value={notesValue}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Context, access details, anything the office should know…"
          />
          {!isLead ? (
            <>
              <label className="cov-row__meta" htmlFor="ct-followup">
                Follow-up plan
              </label>
              <textarea
                id="ct-followup"
                className="ct-textarea ct-textarea--short"
                value={followupValue}
                onChange={(e) => {
                  const existing =
                    formData.extended_fields && typeof formData.extended_fields === "object"
                      ? formData.extended_fields
                      : {};
                  update("extended_fields", { ...existing, follow_up: e.target.value });
                }}
                placeholder="Next step, who owns it, and when…"
              />
            </>
          ) : null}
          <div className="cov-more">Saved when you click Save below.</div>
        </div>
      </section>
    </div>
  );
}
